import {
	CellClickedEvent,
	ColDef,
	GetRowIdParams,
	SelectionChangedEvent,
	SizeColumnsToContentStrategy,
	ValueGetterParams,
} from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CycleManager } from '@treams/treams-analytics';
import { MomentType } from '@treams/treams-network';
import {
	DataGrid,
	createDataGridMultiRowSelection,
	createDataGridSelectionColumnDef,
	handleDataGridSelectionChanged,
	isDataGridSelectionColumn,
} from '@treams/treams-ui';

import { ratio } from '../CycleAnalytics';
import { GridProgressCell } from './GridProgressCell';
import { Manager } from './RemindManagersModal';
import {
	CycleColumn,
	ManagerRow,
	buildManagerColumns,
	buildManagerRows,
	isProgressComplete,
	managerName,
} from './cycleColumns';

// The departments column can hold a long, comma-separated list, so let it grow
// to fit its content and rely on horizontal scrolling instead of squeezing it.
const autoSizeStrategy: SizeColumnsToContentStrategy = {
	type: 'fitCellContents',
	colIds: ['departments'],
};

const defaultColDef: ColDef<ManagerRow> = {
	sortingOrder: ['asc', 'desc'],
};

// Layout that only applies to the grid, keyed by shared column id.
const gridColumnLayout: Record<string, ColDef<ManagerRow>> = {
	manager: { initialSort: 'asc', flex: 2, minWidth: 200 },
	// No `flex` here: the column auto-sizes to its (potentially long) content via
	// `autoSizeStrategy`, so the grid scrolls horizontally instead of cramming
	// everything into the viewport.
	departments: { minWidth: 200, flex: 0 },
};

function toColDef(column: CycleColumn<ManagerRow>): ColDef<ManagerRow> {
	if (column.type === 'fraction') {
		return {
			colId: column.id,
			headerName: column.header,
			minWidth: 130,
			valueGetter: ({ data }: ValueGetterParams<ManagerRow>) => {
				const fraction = data ? column.value(data) : null;

				return fraction ? ratio(fraction.value, fraction.total) : null;
			},
			cellRenderer: ({ data }: CustomCellRendererProps<ManagerRow>) => {
				const fraction = data ? column.value(data) : null;

				return fraction ? <GridProgressCell value={fraction.value} total={fraction.total} /> : null;
			},
		};
	}

	const { sortValue } = column;

	return {
		colId: column.id,
		headerName: column.header,
		// Sort/filter on `sortValue` where the visible text carries extra content
		// (the departments "N users" suffix), but always display the text.
		valueGetter: ({ data }: ValueGetterParams<ManagerRow>) =>
			data ? (sortValue ? sortValue(data) : column.value(data)) : '',
		cellRenderer: ({ data }: CustomCellRendererProps<ManagerRow>) =>
			data ? column.value(data) : null,
		...gridColumnLayout[column.id],
	};
}

function toManagerRowManager(data: ManagerRow): Manager {
	return {
		id: data.user.id,
		name: managerName(data.user),
	};
}

export function CycleManagersGrid({
	managers,
	activeGroupIDs,
	momentID,
	momentType,
	onSelectManager,
	onSelectedManagersChange,
}: {
	managers: CycleManager[];
	// The analytics snapshot doesn't carry a group's archived state, so we filter
	// managed departments against the currently-active group ids.
	activeGroupIDs: Set<string>;
	momentID: string | undefined;
	momentType: MomentType | undefined;
	onSelectManager: (managerID: string) => void;
	onSelectedManagersChange: (managers: Manager[]) => void;
}) {
	const { t } = useTranslation();

	const rowData = useMemo(
		() => buildManagerRows(managers, momentID, activeGroupIDs),
		[managers, activeGroupIDs, momentID],
	);

	const columnDefs = useMemo<ColDef<ManagerRow>[]>(
		() => [
			createDataGridSelectionColumnDef<ManagerRow>({
				disabledTooltip: t('analytics.cycles.managers.selectionDisabled'),
			}),
			...buildManagerColumns({ rowData, momentType, t }).map(toColDef),
		],
		[rowData, t, momentType],
	);

	const handleCellClicked = (event: CellClickedEvent<ManagerRow>) => {
		if (isDataGridSelectionColumn(event.column) || !event.data) {
			return;
		}

		onSelectManager(event.data.user.id);
	};
	const getRowId = useCallback(({ data }: GetRowIdParams<ManagerRow>) => data?.user.id ?? '', []);

	const rowSelection = useMemo(
		() =>
			createDataGridMultiRowSelection<ManagerRow>({
				isRowSelectable: ({ data }) => (data ? !isProgressComplete(data.progress) : false),
			}),
		[],
	);

	const handleSelectionChanged = (event: SelectionChangedEvent<ManagerRow>) => {
		handleDataGridSelectionChanged(event, (rows) =>
			onSelectedManagersChange(rows.map((row) => toManagerRowManager(row))),
		);
	};

	return (
		<DataGrid<ManagerRow>
			key={momentID}
			rowData={rowData}
			columnDefs={columnDefs}
			defaultColDef={defaultColDef}
			autoSizeStrategy={autoSizeStrategy}
			getRowId={getRowId}
			rowSelection={rowSelection}
			onSelectionChanged={handleSelectionChanged}
			onCellClicked={handleCellClicked}
			rowStyle={{ cursor: 'pointer' }}
		/>
	);
}
